import { useState, useEffect, useContext } from "react";
import {
  Table,
  InputNumber,
  Modal,
  Select,
  Radio,
  Form,
  Space,
  notification,
  Tooltip,
} from "antd";
import {
  PlusCircleTwoTone,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import { Context } from "../../context";
import { Delete, Fetch, Patch, Post, Put } from "../../fetch";
import {
  convertIdToKey,
  removeUnpublished,
  convertIdtoSeriesName,
} from "../../utils";
import Column from "antd/lib/table/Column";
import UploadBanners from "../UploadBanners/index";

const { Option } = Select;
const { confirm } = Modal;

const ManageBanners = () => {
  const { apiUrl, token } = useContext(Context);

  const [form] = Form.useForm();
  const [page, setPage] = useState("home");
  const [banners, setBanners] = useState([]);
  const [bannerType, setBannerType] = useState("standalone");
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [songs, setSongs] = useState([]);
  const [bannerImageUrl, setBannerImageUrl] = useState();
  const [counter, setCounter] = useState(0);
  const [promoId, setPromoId] = useState();

  useEffect(() => {
    if (page) {
      Fetch(`${apiUrl}/promos?filter=${page}`, token, (result) => {
        const banners = convertIdToKey(result.promos);
        setBanners(banners);
      });
    }

    Fetch(`${apiUrl}/movies`, token, (result) => {
      const movies = removeUnpublished(convertIdToKey(result.movies));
      setMovies(movies);
    });

    Fetch(`${apiUrl}/series`, token, (result) => {
      const series = convertIdToKey(result.series);
      Fetch(`${apiUrl}/seasons`, token, (result) => {
        if (result.success) {
          const seasons = removeUnpublished(
            convertIdtoSeriesName(series, result.seasons)
          );
          setSeasons(seasons);
        }
      });
      setSeries(series);
    });
  }, [apiUrl, token, page, counter]);

  const handleSubmit = async () => {
    setConfirmLoading(true);
    try {
      const values = await form.validateFields();
      const fn = modalType === "Add" ? Post : Patch;
      fn(
        modalType === "Add"
          ? `${apiUrl}/promos`
          : `${apiUrl}/promos/${promoId}`,
        token,
        { ...values, page },
        (result) => {
          if (result.success === true) {
            notification.success({
              message: "Success",
              description: "Banners updated",
            });
            form.resetFields();
            setModalVisible(false);
            setConfirmLoading(false);
            setCounter(counter + 1);
            setBannerType("standalone");
          } else if (result.success === false) {
            setConfirmLoading(false);
            notification.error({
              message: "Error",
              description: result["message"],
            });
          }
        },
        () => {
          notification.error({
            message: "Edit Banners Error",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
      notification.error({
        message: "Edit Banners Error",
        description: "Required fields are incorrect/missing",
      });
    } finally {
      setConfirmLoading(false);
      setBannerImageUrl("");
    }
  };

  const extractNameByType = (record) => {
    if (
      "movie" in record &&
      ["movies", "home", "movies-kids"].includes(record.page)
    ) {
      for (const movie of movies) {
        if (movie.key === record.movie) {
          return movie.title;
        }
      }
    } else if (
      "season" in record &&
      ["series", "home", "series-kids"].includes(record.page)
    ) {
      for (const s of seasons) {
        if (s._id === record.season) {
          return s.enrichedName;
        }
      }
    } else return "-";
  };

  const showConfirmDelete = (id) => {
    confirm({
      title: "Are you sure you want to delete this banner?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        Delete(
          `${apiUrl}/promos/${id}`,
          token,
          (result) => {
            if (result.success) {
              notification.success({
                message: "Success",
                description: "Promo deleted successfully",
              });
              setCounter(counter + 1);
            }
          },
          () => {
            notification.error({
              message: "Error deleting promo",
              description: "Please check console for more details",
            });
          }
        );
      },
    });
  };

  const showEditModal = (record) => {
    setModalType("Edit");
    setPromoId(record.key);
    form.setFieldsValue(record);
    setBannerImageUrl(record.bannerImage);
    setModalVisible(true);

    if (page === "home") {
      setBannerType(record.promoType);
    }
  };

  const filterMaturity = (video, page) => {
    if (page.includes("kids")) {
      return video.maturity === "U";
    }
    return true;
  };

  return (
    <div className="manage-episodes-wrapper">
      {/* <Space direction='horizontal' size={"middle"}>
        <Select
          style={{ width: 200 }}
          placeholder="Select Page"
          showSearch
          onChange={handleChange}
        >
          <Option value="home">Home</Option>
          <Option value="songs">Songs</Option>
          <Option value="home-kids">Home Kids</Option>
          <Option value="movies-kids">Movies Kids</Option>
          <Option value="series-kids">Series Kids</Option>
          <Option value="songs-kids">Songs Kids</Option>
        </Select>
      </Space> */}
      <Table dataSource={banners}>
        {(page === "home" || page === "home-kids") && (
          <Column
            title="Promo Type"
            dataIndex="promoType"
            key="promoType"
            align="center"
          />
        )}
        <Column
          title="Name"
          render={(_, record) => {
            let title = extractNameByType(record);
            if (title !== undefined) {
              if (title.length > 50) {
                return (
                  <Tooltip title={title}>
                    <span>{`${title.slice(0, 50)}...`}</span>
                  </Tooltip>
                );
              } else {
                return title;
              }
            } else {
              return "-";
            }
          }}
          align="center"
        />
        <Column
          title="Position"
          dataIndex="position"
          key="position"
          align="center"
        />
        <Column
          title="Banner"
          dataIndex="bannerImage"
          key="bannerImage"
          align="center"
          render={(value) => <img src={value} style={{ width: "250px" }} />}
        />
        <Column
          title="Actions"
          key="action"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <a
                onClick={() => {
                  showEditModal(record);
                }}
              >
                Edit
              </a>
              <a onClick={() => showConfirmDelete(record.key)}>Delete</a>
            </Space>
          )}
        />
      </Table>
      <PlusCircleTwoTone
        twoToneColor="#1890FF"
        style={{ fontSize: "48px" }}
        className="add-series-btn"
        onClick={() => {
          setModalVisible(true);
          setModalType("Add");
        }}
      />
      <Modal
        title="Modify Banner"
        open={modalVisible}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={() => {
          setModalVisible(false);
          setBannerType("standalone");
          form.resetFields();
        }}
        confirmLoading={confirmLoading}
        maskClosable={false}
        destroyOnClose={true}
      >
        <Form layout="vertical" form={form}>
          {(page === "home" || page === "home-kids") && (
            <Form.Item
              name="promoType"
              label="Type"
              rules={[{ required: true }]}
              initialValue={"standalone"}
            >
              <Radio.Group
                className="content-type-option"
                onChange={(e) => setBannerType(e.target.value)}
                defaultValue={"standalone"}
              >
                <Radio value="standalone">Standalone</Radio>
                <Radio value="season">Season</Radio>
                <Radio value="static">Static</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          {(page === "standalone" ||
            page === "movies-kids" ||
            bannerType === "standalone") && (
            <Form.Item
              name="movie"
              label="Select Content"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                className="form-card-selector"
                optionFilterProp="children"
              >
                {movies
                  .filter((video) => filterMaturity(video, page))
                  .map((movie) => (
                    <Option key={movie.key} value={movie.key}>
                      {movie.title}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {(page === "season" ||
            page === "series-kids" ||
            bannerType === "season") && (
            <Form.Item
              name="season"
              label="Select Content"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                className="form-card-selector"
              >
                {seasons.map((s) => (
                  <Option key={s._id} value={s._id}>
                    {s.enrichedName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="position"
            label="Position"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item
            name="bannerImage"
            label="Banner"
            rules={[{ required: bannerType === "static" }]}
          >
            <UploadBanners name={"bannerImage"} updateForm={form} />
          </Form.Item>
          <Form.Item
            name="mobileBanner"
            label="Mobile Banner"
            rules={[{ required: bannerType === "static" }]}
          >
            <UploadBanners name={"mobileBanner"} updateForm={form} />
          </Form.Item>
          {/* <Form.Item name="mobileImage" label="Mobile Banner" rules={[{ required: true }]}>
            <UploadBanners name={"mobileImage"} updateForm={form} />
          </Form.Item> */}
        </Form>
      </Modal>
    </div>
  );
};

export default ManageBanners;
